import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateConfig } from "../../../../store/slices/User/PopularSearch/popularSearchSlice";
import { toast } from "react-toastify";
import { createPopularSearch } from "../api";
import { useGetPopularSearchBasicDataQuery } from "../../../../store/queries/User";
import useValidations from "../../../../utils/hooks/useValidations";
import dayjs from "dayjs";

const useAddPopularSearch = (handleCreateClick, refetch) => {
  const dispatch = useDispatch();
  const [isType, setType] = useState(null);
  const { englishRegex, arabicRegex } = useValidations();
  const { data: basicData = {}, isLoading: basicLoading } =
    useGetPopularSearchBasicDataQuery();
  const { isEdit, editPopularSearchData } = useSelector(
    (state) => state.popularSearch
  );

  const { isLoading } = useSelector((state) => state.popularSearch);
  const validation = Yup.object({
    search_text: Yup.string()
      .trim()
      .matches(englishRegex, "Enter valid Search Text")
      .required("Enter Search Text"),
    alias_search_text: Yup.string()
      .trim()
      .matches(arabicRegex, "Enter valid Search Text")
      .required("Enter Search Text"),
    search_type: Yup.object().required("Select type"),
    start_time: isType === 2 && Yup.string().required("Please select start time"),
    end_time:
      isType === 2 &&
      Yup.mixed()
        .nullable()
        .test("is-valid-or-null", "Invalid time", function (value) {
          const { start_time } = this.parent;
          if (!start_time || value === null) return true; // Skip if start_time is not set
          return dayjs.isDayjs(value); // Ensure to_hour is dayjs if set
        })
        .test(
          "required-if-from-hour",
          "End time is required",
          function (end_time) {
            const { start_time } = this.parent;
            // Only require end_time if start_time is set
            if (start_time && (end_time === null || end_time === undefined)) {
              return false; // end_time is required in this case
            }
            return true; // Not required otherwise
          }
        )
        .test(
          "is-after-from-hour",
          "End time must be at least 1 minute after From hour",
          function (end_time) {
            const { start_time } = this.parent;

            if (!dayjs.isDayjs(start_time) || !dayjs.isDayjs(end_time)) {
              return true; // Skip test if invalid
            }

            return end_time.diff(start_time, "minute") >= 1;
          }
        ),
  });

  const formik = useFormik({
    initialValues: {
      search_text: "",
      alias_search_text: "",
      status: 2,
      search_type: "",
      start_time: null,
      end_time: null,
    },
    validationSchema: validation,
    onSubmit: (values) => {
      const formattedFromHour = values.start_time
        ? values.start_time.format("hh:mm A")
        : null;
      const formattedToHour = values.end_time
        ? values.end_time.format("hh:mm A")
        : null;
      let data = {
        search_text: values?.search_text ?? "",
        alias_search_text: values?.alias_search_text ?? "",
        status: values?.status ?? 2,
        search_type: values?.search_type?.id ?? "",
        start_time: formattedFromHour,
        end_time: formattedToHour,
      };
      let newObj = Object.entries(data);
      let subData = newObj
        .filter(
          (item) => item[1] !== undefined && item[1] !== "" && item[1] !== null
        )
        .reduce((a, v) => ({ ...a, [v[0]]: v[1] }), {});
      dispatch(
        updateConfig((state) => {
          state.isLoading = true;
        })
      );

      if (!isEdit) {
        createPopularSearch(subData).then((response) => {
          if (response?.data?.status_code === 200) {
            toast.success(`Popular Search created successfully`);
            refetch();
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          } else if (response?.data?.status_code === 422 && response?.data?.errors) {
            Object.keys(response?.data?.errors).forEach((field) => {
              formik.setFieldError(field, response?.data?.errors[field]);
            });
            dispatch(
              updateConfig((state) => {
                state.isLoading = false;
              })
            );
          } else {
            toast.error(`Something went wrong!`);
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          }
        });
      } else {
        let updatePopularSearchData = {
          search_id: editPopularSearchData?._id,
          ...subData,
        };

        createPopularSearch(updatePopularSearchData).then((response) => {
          if (response?.data?.status_code === 200) {
            toast.success(`Popular Search updated successfully`);
            refetch();
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
                state.editPopularSearchData = {};
              })
            );
          } else if (response?.data?.status_code === 422 && response?.data?.errors) {
            Object.keys(response?.data?.errors).forEach((field) => {
              formik.setFieldError(field, response?.data?.errors[field]);
            });
            dispatch(
              updateConfig((state) => {
                state.isLoading = false;
              })
            );
          } else {
            toast.error(`Something went wrong!`);
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          }
        });
      }
    },
  });

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };
  const closeModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = false;
        state.isEdit = false;
        state.editPopularSearchData = {};
      })
    );
  };

  useEffect(() => {
    let selectVal = basicData?.data?.type_list?.find(
      (item) => item?.id === editPopularSearchData?.search_type
    );
    if (isEdit && editPopularSearchData) {
      formik.setValues({
        search_text: editPopularSearchData?.search_text || "",
        alias_search_text: editPopularSearchData?.alias_search_text || "",
        status: editPopularSearchData?.status || 2,
        search_type: selectVal,
        start_time: editPopularSearchData.start_time
          ? dayjs(editPopularSearchData.start_time, "hh:mm A")
          : null,
        end_time: editPopularSearchData.end_time
          ? dayjs(editPopularSearchData.end_time, "hh:mm A")
          : null,
      });
    }
    // eslint-disable-next-line
  }, [isEdit, editPopularSearchData, basicLoading]);

  const handleTypeChange = (e) => {
    formik?.setFieldValue("search_type", e);
    setType(e?.value);
    if (!isEdit) {
    formik?.setFieldValue("start_time", "");
    formik?.setFieldValue("end_time", "");
     }
  };

  useEffect(() => {
    if (!formik.values.start_time) {
      formik.setFieldValue("end_time", null);
    }
  }, [formik.values.start_time]);

  return {
    isEdit,
    formik,
    isLoading,
    basicData,
    editPopularSearchData,
    handleTypeChange,
    getFieldError,
    closeModal,
  };
};

export default useAddPopularSearch;
